import * as AK from '@ariakit/react'
import classNames from 'classnames'
import {Text} from 'quickstart/components/content/Text'
import {ClearButton} from 'quickstart/components/controls/ClearButton'
import {Box} from 'quickstart/components/layout/Box'
import {Shape} from 'quickstart/components/layout/Shape'
import {useEffectCallback, useHydrating} from 'quickstart/hooks'
import {dataProps} from 'quickstart/theme'
import * as R from 'rambdax'
import {ComponentProps, useEffect} from 'react'
import {SetRequired} from 'type-fest'
import S from './styles.module.scss'

// exported as Modal.useStore
const useModalStore = (options?: AK.DialogStoreProps) =>
  AK.useDialogStore({animated: true, ...options})

type ModalProps = SetRequired<ComponentProps<typeof AK.Dialog>, 'store'> & {
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'auto'
  onClose?: () => void | undefined
}

const _Modal = ({
  children,
  onClose,
  size = 'lg',
  store,
  ...props
}: ModalProps) => {
  const {mounted} = AK.useStoreState(store)

  onClose = useEffectCallback(onClose)
  useEffect(() => {
    if (mounted) return onClose
  }, [mounted, onClose])

  const rendered = (
    <AK.Dialog
      store={store}
      backdrop={false}
      render={({className: dialogClassName, ...dialogProps}) => (
        <div
          className={S.backdrop}
          style={{display: mounted ? undefined : 'none'}}
          {...R.pick(['data-enter'], dialogProps)}
        >
          <div
            className={classNames(S.dialog, dialogClassName)}
            {...dataProps({size})}
            {...dialogProps}
          />
        </div>
      )}
      {...props}
    >
      <ClearButton className={S.close} onClick={store.hide} />
      {children}
    </AK.Dialog>
  )

  // https://github.com/facebook/react/issues/12615
  return useHydrating() ? null : rendered
}

const Content = ({className, ...props}: ComponentProps<typeof Box>) => (
  <Box className={classNames(S.content, className)} {...props} />
)

const Title = ({className, ...props}: ComponentProps<typeof Text>) => (
  <Text variant="modalHead" {...props} className={classNames(S.title, className)} />
)

const Footer = ({className, ...props}: ComponentProps<typeof Box>) => (
  <Box className={classNames(S.footer, className)} {...props} />
)

const Cover = ({className, ...props}: ComponentProps<typeof Shape>) => (
  <Shape className={classNames(S.cover, className)} {...props} />
)

export const Modal = Object.assign(_Modal, {
  Content,
  Title,
  Footer,
  Cover,
  useStore: useModalStore,
})
